import { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  ActionTabs,
  AppIcon,
  buildSeededMeshConfig,
  ConfirmDelete,
  DonutChart,
  FullScreenLoading,
  MeshGradient,
  NotFound404,
  NuggetBadge,
  PopoverFilterMenu,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useAddSuggestedPlaylistByIdMutation,
  useDeletePlaylistMutation,
  useGetPlaylistByIdQuery,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  openEditPlaylistDialog,
  openPlaylistViewStep,
  openSuggestedPlaylistViewStep,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { useAgentContext } from './PlaybookPageContainer.agentContext';
import { useGetTaskActionsAndProperties } from '../hooks/tasks.hooks';
import { PlaylistTaskList, usePlaylistTaskList } from '../molecules/PlaylistTaskList';
import { getPlaylistPercentageFilled } from '../utils/playlist.utils';

const STRINGS = appStrings.modules.priorities.playbookPage;
const DRAWER_STRINGS = appStrings.modules.priorities.playlistDrawer;
const TEMPLATE_STRINGS = appStrings.modules.priorities.playlistTemplateDrawer;

export const PlaybookPageContainer = () => {
  const { playbookId } = useParams<{ playbookId: string }>();
  const playlistId = Number(playbookId);
  const hasValidId = Number.isInteger(playlistId) && playlistId > 0;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const {
    data: playlist,
    isLoading,
    isError,
  } = useGetPlaylistByIdQuery({ id: playlistId }, { skip: !hasValidId });

  const isTemplate = !!playlist?.is_template;

  const [deletePlaylist] = useDeletePlaylistMutation();
  const [addSuggestedPlaylist, { isLoading: isAdding }] = useAddSuggestedPlaylistByIdMutation();
  const { handleOpenNewTaskDialog } = useGetTaskActionsAndProperties();
  const taskList = usePlaylistTaskList(hasValidId ? playlistId : null, isTemplate);

  usePageTitle(playlist?.title);
  useAgentContext(playlist, taskList.visibleTasks);

  if (isLoading) {
    return <FullScreenLoading />;
  }

  if (!hasValidId || isError || !playlist) {
    return <NotFound404 />;
  }

  const handleDeletePlaylist = async () => {
    try {
      await deletePlaylist(playlist.id).unwrap();
    } catch (error) {
      console.error('Failed to delete playbook:', error);

      return;
    }

    setConfirmDeleteOpen(false);
    navigate(APP_ROUTE.PRIORITIES);
  };

  const handleAddPlaylist = async () => {
    try {
      const newPlaylist = await addSuggestedPlaylist(playlist.id).unwrap();

      navigate(APP_ROUTE.PLAYBOOK.replace(':playbookId', String(newPlaylist.id)));
    } catch (error) {
      console.error('Failed to add suggested playlist:', error);
    }
  };

  const handleShare = async () => {
    await navigator.clipboard.writeText(`${GROW_URL}${pathname}`);
  };

  return (
    <Box sx={pageSx}>
      <ConfirmDelete
        openDialog={confirmDeleteOpen}
        setOpenDialog={setConfirmDeleteOpen}
        headerText={DRAWER_STRINGS.deletePlaylist}
        bodyText={DRAWER_STRINGS.deletePlaylistConfirmBody}
        handleDelete={handleDeletePlaylist}
      />

      <PopoverFilterMenu
        anchorEl={sortAnchorEl}
        setAnchorEl={setSortAnchorEl}
        title={STRINGS.sortBy}
        categories={[{ label: DRAWER_STRINGS.sortOptions, items: taskList.wrappedSortMenuItems }]}
        singleLevel
      />

      <Box sx={bannerCardSx}>
        <Box sx={bannerMediaSx}>
          {playlist.preview_media?.media_url ? (
            <Box
              id="playbook-image"
              component="img"
              src={playlist.preview_media.media_url}
              alt=""
              sx={bannerImageSx}
            />
          ) : (
            <Box id="playbook-image-placeholder" sx={bannerFallbackSx}>
              <MeshGradient config={buildSeededMeshConfig(playlist.id)} />
            </Box>
          )}

          <Box sx={bannerBackSx}>
            <Button
              id="playbook-back-link"
              component={RouterLink}
              to={APP_ROUTE.PRIORITIES}
              variant="contained"
              hierarchy="Secondary gray"
              size="sm"
              startIcon={<AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />}
            >
              {STRINGS.backToPlaybooks}
            </Button>
          </Box>

          <Box sx={bannerChipSx} id="playbook-type-chip">
            <NuggetBadge label={STRINGS.playbookBadge} iconConfig={{ mode: 'backrsLogo' }} />
          </Box>

          <Box sx={bannerIconGroupSx}>
            <ActionTabs
              borderColor="Colors/Border/border-primary"
              showCopySuccess={showCopySuccess}
              onCopySuccessChange={setShowCopySuccess}
              actions={[
                // Only a template is readable by anyone else, and only your own
                // playbook is yours to delete.
                ...(isTemplate
                  ? [
                      {
                        id: 'playbook-share-button',
                        icon: 'Share06' as const,
                        onClick: handleShare,
                        tooltipHoverText: STRINGS.share,
                        tooltipOnClickText: STRINGS.linkCopied,
                        tooltipPosition: 'top' as const,
                      },
                    ]
                  : [
                      {
                        id: 'playbook-delete-button',
                        icon: 'Trash01' as const,
                        onClick: () => setConfirmDeleteOpen(true),
                        tooltipHoverText: STRINGS.delete,
                      },
                    ]),
                {
                  id: 'playbook-open-sidebar-button',
                  icon: 'LogIn03' as const,
                  onClick: () => {
                    dispatch(
                      isTemplate
                        ? openSuggestedPlaylistViewStep(playlist)
                        : openPlaylistViewStep(playlist),
                    );
                  },
                  tooltipHoverText: STRINGS.openInSidebar,
                },
              ]}
            />
          </Box>
        </Box>

        <Box sx={bannerBodySx}>
          <DonutChart
            variant={{ type: 'solo', percentageFilled: getPlaylistPercentageFilled(playlist) }}
            size={64}
            isTooltipEnabled={false}
          />

          <Box sx={bannerTextSx}>
            <Box sx={bannerTitleRowSx}>
              <Typography variant="display-xs-semibold" sx={bannerTitleSx}>
                {playlist.title}
              </Typography>
              {!isTemplate && (
                <Tooltip title={STRINGS.edit} disableInteractive>
                  <IconButton
                    id="playbook-edit-title-button"
                    size="small"
                    onClick={() => dispatch(openEditPlaylistDialog(playlist))}
                    aria-label={STRINGS.edit}
                  >
                    <AppIcon
                      iconName="Pencil01"
                      diameter="20px"
                      color="Colors/Foreground/fg-quaternary"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {playlist.description && (
              <Typography variant="text-md-regular" sx={bannerSubtitleSx}>
                {playlist.description}
              </Typography>
            )}
          </Box>

          <Box sx={bannerActionsSx}>
            {isTemplate ? (
              <Button
                id="playbook-add-button"
                variant="contained"
                hierarchy="Primary"
                size="md"
                onClick={handleAddPlaylist}
                disabled={isAdding}
                startIcon={<AppIcon iconName="Plus" diameter="20px" color="currentColor" />}
              >
                {isAdding ? TEMPLATE_STRINGS.addingPlaylist : TEMPLATE_STRINGS.addPlaylist}
              </Button>
            ) : (
              <Button
                id="playbook-new-task-button"
                variant="contained"
                hierarchy="Primary"
                size="md"
                onClick={() => handleOpenNewTaskDialog(playlist, false)}
                startIcon={
                  <AppIcon iconName="ClipboardPlus" diameter="20px" color="currentColor" />
                }
              >
                {STRINGS.newTask}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={listControlsSx}>
        <ClickAwayListener onClickAway={() => null}>
          <Button
            id="playbook-sort-button"
            variant="contained"
            hierarchy="Secondary gray"
            size="md"
            icon="Leading"
            onClick={(event) => setSortAnchorEl(event.currentTarget)}
            startIcon={
              <AppIcon
                iconName="FilterLines"
                diameter="20px"
                color="Colors/Foreground/fg-primary"
              />
            }
          >
            {STRINGS.sortBy}
          </Button>
        </ClickAwayListener>

        {!isTemplate && (
          <Button
            id="playbook-reorder-button"
            variant="contained"
            hierarchy={taskList.isReorderMode ? 'Primary' : 'Secondary gray'}
            size="md"
            icon="Leading"
            onClick={taskList.handleToggleReorderMode}
            startIcon={<AppIcon iconName="DotPoints02" diameter="20px" color="currentColor" />}
          >
            {taskList.isReorderMode ? STRINGS.doneReordering : STRINGS.reorder}
          </Button>
        )}
      </Box>

      <PlaylistTaskList
        playlist={playlist}
        state={taskList}
        variant="row"
        readOnly={isTemplate}
        onOpenTask={(task) => {
          const path = APP_ROUTE.PLAYBOOK_TASK.replace(':taskId', String(task.id));

          // A template's tasks aren't the viewer's, so the task page can't look them
          // up by id alone; the playbook it came from is how it resolves them.
          navigate(isTemplate ? `${path}?playbook=${playlist.id}` : path);
        }}
        containerSx={taskListSx}
        skeletonWidth="100%"
      />
    </Box>
  );
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
  py: backrs.spacing['spacing-2xl'],
});

const bannerCardSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  width: '100%',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const BANNER_MEDIA_HEIGHT = 240;

const bannerMediaSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: '100%',
  height: { xs: 180, sm: BANNER_MEDIA_HEIGHT },
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
});

// The back button and the action icons share the top of the banner; at phone width the
// full inset leaves them touching, so tighten it.
const bannerInset = (backrs: Theme['backrs']) => ({
  xs: backrs.spacing['spacing-md'],
  sm: backrs.spacing['spacing-xl'],
});

const bannerImageSx: SxProps<Theme> = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

// The gradient paints itself absolutely, so it needs a box of the banner's size to fill.
const bannerFallbackSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
};

const bannerBackSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: bannerInset(backrs),
  left: bannerInset(backrs),
});

const bannerChipSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  bottom: bannerInset(backrs),
  left: bannerInset(backrs),
});

const bannerIconGroupSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: bannerInset(backrs),
  right: bannerInset(backrs),
});

const bannerTitleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const bannerBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'flex-start', sm: 'center' },
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-2xl'],
});

const bannerTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
  minWidth: 0,
});

const bannerTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  wordBreak: 'break-word',
});

const bannerSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const bannerActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  flexShrink: 0,
});

const listControlsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
});

const taskListSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xl'],
  pb: backrs.spacing['spacing-3xl'],
});
